import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Activity as ActivityIcon,
  AlertTriangle,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { relativeTime, formatDateTime } from "@/components/ux/relativeTime";
import { activityApi, type ActivityEvent } from "../api/activity";
import { useDateFormatter } from "@/utils/datetime";
import { AuthContext } from "@/hooks/authContext";
import { dealTypeActivityPrequalLink } from "@/components/deals/dealTypeActivity";

export interface ActivityTabPanelProps {
  ownerType: string;
  ownerId: string;
  /** Events shown on first render; each "Load more" click reveals another
   * page of this size. Default 10. */
  pageSize?: number;
}

const PAGE_SIZE_DEFAULT = 10;

/**
 * Activity Timeline v1.0 — read-only chronological feed rendered inside the
 * Activity tab on every entity detail page (Layout Standard §4.8). One merged
 * reverse-chronological list from the cross-module aggregator: no source
 * filter pills, no facets. Older rows are reachable via "Load more", which
 * grows the page and appends the next slice (the per-owner aggregator feed is
 * offset-paged and unbounded by recency).
 *
 * Read-only on purpose: there is no composer, no inline edit, no delete
 * affordance. Notes go on the Notes tab; Activity is the audit story.
 */
export function ActivityTabPanel({
  ownerType,
  ownerId,
  pageSize = PAGE_SIZE_DEFAULT,
}: ActivityTabPanelProps) {
  const [steps, setSteps] = useState(1);
  const take = steps * pageSize;

  const query = useQuery({
    queryKey: ["activity", "by-owner", ownerType, ownerId, "feed", take],
    queryFn: () => activityApi.listByOwner(ownerType, ownerId, { take }),
    enabled: Boolean(ownerType) && Boolean(ownerId),
    placeholderData: keepPreviousData,
  });

  const events = query.data?.data ?? [];
  const total = query.data?.totalCount ?? 0;
  const loading = query.isLoading;
  const loadingMore = query.isFetching && !query.isLoading;
  const hasMore = events.length < total;
  const failed = query.isError;

  return (
    <section
      aria-label="Activity"
      className="flex flex-col gap-space-4"
      data-testid="activity-tab-panel"
    >
      {loading && <SkeletonList />}

      {/* 🔴 A failed read used to fall straight through to "No activity yet." —
          a confident claim about the record, made on the strength of a request
          that never completed, with nothing on screen to say so and no way to
          retry. Worse here than elsewhere: the shared QueryClient runs at
          `staleTime: 5min` with `refetchOnWindowFocus: false`, so without this
          the only route back is a full page reload.

          Rendered ABOVE any rows rather than instead of them: with
          `keepPreviousData` a failed "Load more" keeps the rows already
          fetched, and those are real — the banner says the newest page is not. */}
      {!loading && failed && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-space-3 rounded-md border border-status-error bg-white-warm px-space-3 py-space-2"
        >
          <AlertTriangle size={14} className="text-status-error shrink-0" aria-hidden />
          <p className="text-small text-onyx m-0 flex-1 min-w-[12rem]">
            Couldn&apos;t load activity. The request failed, so nothing here is a
            statement about what is on file.
          </p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            data-testid="activity-retry"
          >
            {query.isFetching ? "Retrying…" : "Try again"}
          </Button>
        </div>
      )}

      {!loading && !failed && events.length === 0 && (
        <div className="rounded-md border border-dashed border-border-light p-space-4 text-small text-gray-mid">
          No activity yet.
        </div>
      )}

      {events.length > 0 && (
        <ol className="flex flex-col gap-space-3 m-0 p-0 list-none">
          {events.map((event) => (
            <ActivityRow key={event.id} event={event} />
          ))}
        </ol>
      )}

      {!loading && hasMore && (
        <div className="flex justify-center">
          <Button
            variant="text"
            size="sm"
            onClick={() => setSteps((s) => s + 1)}
            disabled={loadingMore}
            data-testid="activity-load-more"
          >
            {loadingMore ? (
              <span className="inline-flex items-center gap-space-1">
                <Loader2 size={14} strokeWidth={1.5} className="animate-spin" aria-hidden />
                Loading…
              </span>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      )}
    </section>
  );
}

interface RowProps {
  event: ActivityEvent;
}

function ActivityRow({ event }: RowProps) {
  const { tz } = useDateFormatter();
  const Icon = resolveIcon(event.icon);
  const occurred = relativeTime(event.occurredAt);
  const showDetails = hasPayload(event.payload);
  // Owner Recapitalization Loan (surface U4) — a deal-type row names the
  // prequalification behind an auto-set; its reference links to that record
  // for a viewer the record's route admits. AuthContext is read directly (as
  // useDateFormatter does) so the feed renders without an AuthProvider.
  const auth = useContext(AuthContext);
  const prequalLink =
    auth?.hasPermission("Prequalification.Read") === true ? dealTypeActivityPrequalLink(event) : null;

  return (
    <li
      className="flex items-start gap-space-2 rounded-md border border-border-light bg-white-pure p-space-3"
      data-testid="activity-row"
    >
      <span
        aria-hidden
        className="h-8 w-8 rounded-pill bg-white-warm text-gold-shadow flex items-center justify-center shrink-0"
      >
        <Icon size={16} strokeWidth={1.5} aria-hidden />
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-space-3">
          {/* Summary — server-formatted text; a deal-type row links the
              prequalification it names (see prequalLink above). */}
          <div className="text-small text-onyx min-w-0">
            {prequalLink ? (
              <>
                {prequalLink.before}
                <Link to={prequalLink.href} className="text-gold-shadow hover:underline">
                  {prequalLink.reference}
                </Link>
                {prequalLink.after}
              </>
            ) : event.clickThroughUrl ? (
              <Link
                to={event.clickThroughUrl}
                className="no-underline text-inherit hover:underline"
              >
                {event.headline}
              </Link>
            ) : (
              event.headline
            )}
          </div>

          {/* Actor avatar + name + relative time. */}
          <div className="flex items-center gap-space-1 shrink-0">
            <ActorAvatar name={event.actorDisplayName} />
            <span className="font-semibold text-small text-onyx truncate">
              {event.actorDisplayName}
            </span>
            <span className="text-label text-gray-mid" title={formatDateTime(event.occurredAt, tz)}>
              · {occurred}
            </span>
          </div>
        </div>

        {event.subline && (
          <div className="text-label text-gray-mid mt-space-1">{event.subline}</div>
        )}

        {showDetails && (
          <details className="mt-space-2" data-testid="activity-details">
            <summary className="cursor-pointer text-label text-gold-shadow hover:underline select-none">
              Show details
            </summary>
            <pre
              className="mt-space-1 overflow-x-auto rounded-sm bg-white-warm border border-border-light px-space-2 py-space-1 text-[11px] leading-snug text-gray-mid font-mono"
              data-testid="activity-payload"
            >
              {formatPayload(event.payload)}
            </pre>
          </details>
        )}
      </div>
    </li>
  );
}

/** Compact actor avatar — initials in a circle. The design system has no
 * generic Avatar primitive (only the task-specific TaskAssigneeAvatar), so the
 * timeline renders its own lightweight badge. */
function ActorAvatar({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      className="h-5 w-5 rounded-pill bg-onyx text-white-pure text-[9px] font-semibold flex items-center justify-center shrink-0"
    >
      {initials(name)}
    </span>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function SkeletonList() {
  return (
    <ol
      className="flex flex-col gap-space-3 m-0 p-0 list-none"
      aria-busy="true"
      aria-label="Loading activity"
      data-testid="activity-skeleton"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="rounded-md border border-border-light bg-white-pure p-space-3 animate-pulse"
        >
          <div className="flex items-start gap-space-2">
            <span className="h-8 w-8 rounded-pill bg-white-warm shrink-0" />
            <div className="flex-1 space-y-space-1">
              <div className="h-3 w-32 bg-white-warm rounded" />
              <div className="h-3 w-2/3 bg-white-warm rounded" />
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** True when the event carries a non-empty structured payload worth a
 * "Show details" disclosure. Treats null, empty string and empty object as
 * "no payload". */
function hasPayload(payload: unknown): boolean {
  if (payload == null) return false;
  if (typeof payload === "string") return payload.trim().length > 0;
  if (typeof payload === "object") return Object.keys(payload as object).length > 0;
  return true;
}

/** Pretty-print the payload for the disclosure. String payloads that happen to
 * be JSON are parsed then re-serialized; everything else renders verbatim. */
function formatPayload(payload: unknown): string {
  if (typeof payload === "string") {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  }
  return JSON.stringify(payload, null, 2);
}

/** Convert a kebab-case lucide icon name into the matching React component,
 * falling back to <Activity /> when the name is unknown. The aggregator
 * returns Lucide-compatible names (e.g. "phone-call", "sticky-note") which we
 * map to PascalCase here. */
function resolveIcon(name: string | null): LucideIcon {
  if (!name) return ActivityIcon;
  const pascal = name
    .split("-")
    .map((part) => (part.length === 0 ? part : part[0].toUpperCase() + part.slice(1)))
    .join("");
  const candidate = (LucideIcons as unknown as Record<string, LucideIcon>)[pascal];
  return candidate ?? ActivityIcon;
}
